import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import Badge from "../../../../../components/elements/Badge";
import DeleteModal from "../../../../../components/common/DeleteModal";
import Table from "../../../../../components/elements/table/Table";
import { setTitle } from "../../../../../helpers/MetaTag";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { getPaginationRange, handleFilterChange } from "../../../../../helpers";
import TableControls from "../../../../../components/elements/TableControls";
import { status_filter } from "../../../../../components/constant/constant";
import { communicationDelete, communicationList, communicationStatusChange } from "../../../../../services/communicationService";
import ReactPortal from "../../../../../components/common/ReactPortal";
import StatusChangeTooltip from "../../../../../components/common/StatusChangeTooltip";

const BlogComment = () => {
  setTitle("Post Comments");
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "createdAt",
  });

  const handleBadgeClick = (e, id) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
    });
    setActiveTooltip((prev) => (prev === id ? null : id));
  };

  const { data, isLoading, updateData, isFetching } = useQuery({
    queryKey: ["blog-comment", filters, search],
    queryFn: async () => {
      const res = await communicationList({
        ...filters,
        ...(search && { keyword: search }),
        purpose: "comment",
        type: "post",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  //  status change handler =================>

  const { mutate: statusChangeHandler, isPending: statusChangeInPending } = useMutation({
    mutationFn: async ({ id, status }) => {
      queryClient.setQueryData(["blog-comment", filters, search], (state) => {
        if (!state) return;

        const updatedData = state?.data?.map((item) =>
          item?._id === id
            ? {
                ...item,
                status: status,
              }
            : item
        );

        return { ...state, data: updatedData };
      });
      const res = await communicationStatusChange({ ids: [id], status });

      return { res, payload: { id, status } };
    },

    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        toast.success("Blog comment status change successfully.");
      } else {
        queryClient.setQueryData(["blog-comment", filters, search], (state) => {
          if (!state) return;

          const revertedData = state?.data?.map((item) =>
            item?._id === payload.id
              ? {
                  ...item,
                  status: item.status,
                }
              : item
          );

          return { ...state, data: revertedData };
        });

        toast.error(res?.message || "Failed to update status.");
      }
    },

    onError: (error) => {
      toast.error(error?.message || "Something went wrong while updating status.");
      console.error("Status update error:", error);
    },
  });

  //  delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await communicationDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["blog-comment", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter((item) => !payload?.includes(item?._id));
            return { ...state, data: stateData };
          }
        });
        toast.success("Blog comment deleted successfully.");

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["job-enquiry", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
      toast.error(error?.message || "Something went wrong while delete Enquiry.");
    },
  });

  const status_multi_filter = [
    ...status_filter,
    { name: "Pending", value: "pending", type: "checkbox" },
    { name: "Closed", value: "closed", type: "checkbox" },
  ];

  const statusArray = [
    {
      state: "pending",
      color: "warning",
    },
    {
      state: "active",
      color: "success",
    },
    {
      state: "inactive",
      color: "danger",
    },
    {
      state: "closed",
      color: "indigo",
    },
  ];

  const tableHeadData = [
    {
      _id: 1,
      name: "Blog",
      align: "left",
    },
    {
      _id: 2,
      name: "User",
      align: "left",
    },
    {
      _id: 3,
      name: "Comment",
      align: "left",
    },
    {
      _id: 4,
      name: "Image comment",
      align: "left",
    },
    {
      _id: 5,
      name: "Commented On",
      align: "left",
      isSort: true,
      onSort: (val) => {
        if (val === "DESC") {
          setFilters((pre) => ({ ...pre, sortQuery: "-createdAt" }));
        } else {
          setFilters((pre) => ({ ...pre, sortQuery: "createdAt" }));
        }
      },
    },

    {
      _id: 6,
      name: "Status",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0 && val?.length === 1) {
          setFilters((pre) => ({
            ...pre,
            status: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.status;
            return { ...pre };
          });
        }
      },
      filterData: status_multi_filter || [],
    },
    {
      _id: 7,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "imageWithText",
          align: "left",
          cellClass: "!min-w-[100px]",
          image: item?.referenceInfo?.featuredImage?.media?.url,
          data: `${item?.referenceInfo?.title}`,
        },
        {
          _id: 2,
          type: "imageWithText",
          align: "left",
          cellClass: "!min-w-[100px]",
          image: item?.userInfo?.image?.url,
          data: `${item?.userInfo?.firstName} ${item?.userInfo?.lastName}`,
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            (
              <span className="relative group inline-block max-w-sm cursor-pointer">
                <span className="inline-flex items-center bg-gray-50 text-gray-700 text-sm font-normal px-3 py-1 rounded-md border border-gray-200 shadow-sm hover:shadow-md transition truncate max-w-full">
                  💬 {item?.content?.slice(0, 25)}
                  {item?.content?.length > 25 ? "…" : ""}
                </span>

                <div
                  className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:flex bg-white text-gray-700 text-sm rounded-md px-4 py-2 shadow-md opacity-90 border border-gray-200 z-20 w-max max-w-xs"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {item?.content}
                </div>
              </span>
            ) || "-",
        },
        {
          _id: 4,
          type: "imageWithText",
          align: "left",
          cellClass: "!min-w-[100px]",
          image: item?.attachmentInfo?.[0]?.url || null,
          data:
            item?.attachments?.length > 1 ? (
              <span className="bg-gray-100 text-gray-800 font-bold text-xs px-2 py-1 rounded-full border border-gray-300">
                +{item?.attachments?.length - 1} more
              </span>
            ) : (
              ""
            ),
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.createdAt ? dayjs(item?.createdAt).format("DD MMM, YYYY") : "-",
        },

        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="relative">
              <div className="flex gap-2 items-center cursor-pointer" onClick={(e) => handleBadgeClick(e, item._id)}>
                <Badge
                  type={statusArray.find((st) => st.state === item?.status)?.color}
                  label={statusArray.find((st) => st.state === item?.status)?.state}
                />
              </div>

              {activeTooltip === item._id && (
                <ReactPortal position={tooltipPosition} onClose={() => setActiveTooltip(null)}>
                  {statusArray.map((status) => {
                    return (
                      <StatusChangeTooltip
                        statusItem={status}
                        itemId={item._id}
                        statusChange={statusChangeHandler}
                        setActiveTooltip={setActiveTooltip}
                        isActive={item.status === status.state}
                      />
                    );
                  })}
                </ReactPortal>
              )}
            </div>
          ),
        },
        {
          _id: 7,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  return (
    <div>
      <div className="space-y-4 w-full flex flex-col pb-10">
        <div className="space-y-8">
          <div className="flex items-center gap-3">
            <TableControls
              limit={filters?.limit}
              totalDocs={data?.metadata?.totalDocs}
              onLimitChange={(val) => handleFilterChange("limit", val, setFilters)}
              onSearch={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
        </div>

        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          totalDataCount={data?.metadata?.totalDocs}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          handleOrdering={(val) => {
            console.log(val);
          }}
          isFetching={isFetching}
          isOrderingDisabled={true}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          selectable
          tableActionDropdownContainer={"!w-48"}
          showingData={getPaginationRange(filters?.page, filters?.limit, data?.metadata?.totalDocs)}
        />
        <DeleteModal
          open={deleteModal?.isOpen}
          loading={deletePending}
          onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
          deleteHandler={() => {
            deleteHandler(deleteModal?.data?._id);
          }}
        />
      </div>
    </div>
  );
};

export default BlogComment;
